import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useData } from '../contexts/DataContext';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Bot, Mail, FileText, Upload, Sparkles, Plus, 
  X, Save, Paperclip, Link as LinkIcon, Calendar, Clock,
  Bell, Video, Trash2, MessageSquare, Send, Edit2, AlertTriangle, Building2, Eye, Users, User,
  BarChart3, Globe, Monitor, MousePointer, Tag, Shield, UserCog, RefreshCw, Smartphone, ExternalLink
} from 'lucide-react';
import { Drawer } from 'vaul';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import LeadFormModal from './LeadFormModal';
import LeadTagsWidget from './LeadTagsWidget';
import WhatsAppButton from './WhatsAppButton';
import StarRating from './StarRating';
import * as api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Modal Portal Component
const ModalPortal = ({ children, isOpen }) => {
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  return ReactDOM.createPortal(children, document.body);
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, itemType, itemName }) => {
  if (!isOpen) return null;
  
  return (
    <ModalPortal isOpen={isOpen}>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4 p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-red-100 p-3 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Confirmar Eliminación
              </h3>
              <p className="text-sm text-slate-500">
                Esta acción no se puede deshacer
              </p>
            </div>
          </div>
          
          <p className="text-slate-700 mb-6">
            ¿Estás seguro de que deseas eliminar {itemType} <strong>&ldquo;{itemName}&rdquo;</strong>?
          </p>
          
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Sí, Eliminar
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

const LeadDetail = () => {
  const { leadId } = useParams();
  const { state, actions } = useData();
  const navigate = useNavigate();
  
  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser?.role === 'Administrador' || currentUser?.role === 'admin';
  const isTrafficker = currentUser?.role === 'trafficker';
  
  // Local state for lead
  const [lead, setLead] = useState(null);
  const [loadingLead, setLoadingLead] = useState(true);
  
  // Check if current user can edit this lead
  // Commercial users can only edit leads assigned to them or unassigned leads
  // Trafficker cannot edit leads (only campaign tags)
  const canEditLead = !isTrafficker && (isAdmin || !lead?.assignedTo || lead?.assignedTo === '' || lead?.assignedTo === currentUser?.id);
  const canDeleteLead = isAdmin; // Only admins can delete leads
  
  // Local state for lead details
  const [activities, setActivities] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(true);
  
  // Lead journey state
  const [leadJourney, setLeadJourney] = useState(null);
  const [loadingJourney, setLoadingJourney] = useState(true);
  
  // Empresa asociada state
  const [empresaAsociada, setEmpresaAsociada] = useState(null);
  const [loadingEmpresa, setLoadingEmpresa] = useState(false);
  
  // Assigned user state
  const [assignedUser, setAssignedUser] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingAssignment, setLoadingAssignment] = useState(false);
  
  // Estado del drawer
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  // Estados para IA
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  
  // Estados para modales de crear
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  
  // Estado para servicios disponibles
  const [availableServices, setAvailableServices] = useState([]);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  
  // Form para crear negocio
  const [dealForm, setDealForm] = useState({
    services: [],
    totalValue: 0,
    estimatedCloseDate: '',
    notes: ''
  });
  
  // Estados para edición
  const [editingItem, setEditingItem] = useState(null);
  const [editingType, setEditingType] = useState(null);
  
  // Estados para eliminar
  const [deletingItem, setDeletingItem] = useState(null);
  const [deletingType, setDeletingType] = useState(null);
  
  // Estados para historial de formularios
  const [showFormHistoryModal, setShowFormHistoryModal] = useState(false);
  const [formHistory, setFormHistory] = useState(null);
  const [loadingFormHistory, setLoadingFormHistory] = useState(false);
  
  // Estados para vista consolidada por email
  const [showConsolidatedModal, setShowConsolidatedModal] = useState(false);
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [loadingConsolidated, setLoadingConsolidated] = useState(false);

  // Forms
  const [activityForm, setActivityForm] = useState({ description: '', type: 'note', activityDate: new Date().toISOString().split('T')[0] });
  const [fileForm, setFileForm] = useState({ name: '', url: '', type: 'link', file: null, fileData: null, fileType: null });
  const [meetingForm, setMeetingForm] = useState({ title: '', date: '', duration: 30, meetingType: 'virtual', location: '', videoLink: '', transcription: '', description: '', additionalAttendees: '' });
  const [alertForm, setAlertForm] = useState({ title: '', description: '', date: '', priority: 'medium', sendEmail: false });

  // Load lead from API
  useEffect(() => {
    const loadLead = async () => {
      setLoadingLead(true);
      try {
        // Always load from API to get complete data including trackingData
        const leadData = await api.getLead(leadId);
        setLead(leadData);
      } catch (error) {
        console.error('Error loading lead:', error);
        setLead(null);
      } finally {
        setLoadingLead(false);
      }
    };
    
    if (leadId) {
      loadLead();
    }
  }, [leadId]);

  // Load lead details
  useEffect(() => {
    const loadDetails = async () => {
      setLoadingDetails(true);
      try {
        const [acts, atts, meets, alts] = await Promise.all([
          api.getActivities(leadId),
          api.getAttachments(leadId),
          api.getMeetings(leadId),
          api.getAlerts(leadId)
        ]);
        setActivities(acts);
        setAttachments(atts);
        setMeetings(meets);
        setAlerts(alts);
      } catch (error) {
        console.error('Error loading details:', error);
      } finally {
        setLoadingDetails(false);
      }
    };
    
    if (leadId) {
      loadDetails();
    }
  }, [leadId]);

  // Load lead journey
  useEffect(() => {
    const loadJourney = async () => {
      setLoadingJourney(true);
      try {
        const journey = await api.getLeadJourney(leadId);
        setLeadJourney(journey);
      } catch (error) {
        console.error('Error loading lead journey:', error);
      } finally {
        setLoadingJourney(false);
      }
    };
    
    if (leadId) {
      loadJourney();
    }
  }, [leadId]);

  // Load empresa asociada when lead has empresaId
  useEffect(() => {
    const loadEmpresa = async () => {
      if (lead?.empresaId) {
        setLoadingEmpresa(true);
        try {
          const empresaData = await api.getEmpresa(lead.empresaId);
          setEmpresaAsociada(empresaData.empresa);
        } catch (error) {
          console.error('Error loading empresa:', error);
          setEmpresaAsociada(null);
        } finally {
          setLoadingEmpresa(false);
        }
      } else {
        setEmpresaAsociada(null);
      }
    };
    
    if (lead) {
      loadEmpresa();
    }
  }, [lead?.empresaId, lead]);

  // Load assigned user and available users
  useEffect(() => {
    const loadAssignmentData = async () => {
      try {
        // Load available users for assignment dropdown
        const usersData = await api.getUsers();
        setAvailableUsers(usersData || []);
        
        // If lead is assigned, find the user
        if (lead?.assignedTo) {
          const assigned = (usersData || []).find(u => u.id === lead.assignedTo);
          setAssignedUser(assigned || null);
        } else {
          setAssignedUser(null);
        }
      } catch (error) {
        console.error('Error loading assignment data:', error);
      }
    };
    
    if (lead) {
      loadAssignmentData();
    }
  }, [lead?.assignedTo, lead]);

  // Load available services for deal creation
  useEffect(() => {
    const loadServices = async () => {
      try {
        const services = await api.getServices();
        setAvailableServices(services || []);
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    loadServices();
    // Load campaigns for trafficker campaign selector
    const loadCampaigns = async () => {
      try { const camps = await api.getMisCampanas(); setAvailableCampaigns(camps || []); } catch {}
    };
    loadCampaigns();
  }, []);

  const pipeline = state.pipelines.find(p => p.id === lead?.pipelineId);
  const stage = pipeline?.stages?.find(s => s.id === lead?.stageId);

  // Create deal from lead
  const handleCreateDeal = async () => {
    if (dealForm.services.length === 0) {
      toast.error('Agregue al menos un servicio');
      return;
    }
    
    try {
      const totalValue = dealForm.services.reduce((acc, s) => acc + (s.value * s.quantity), 0);
      const result = await api.createDealFromLead(leadId, {
        services: dealForm.services,
        totalValue,
        estimatedCloseDate: dealForm.estimatedCloseDate,
        notes: dealForm.notes
      });
      
      toast.success('Negocio creado exitosamente');
      setShowDealModal(false);
      setDealForm({ services: [], totalValue: 0, estimatedCloseDate: '', notes: '' });
      
      // Reload lead journey to show the new deal
      const journey = await api.getLeadJourney(leadId);
      setLeadJourney(journey);
      
      // Reload lead to update empresaId if created
      const updatedLead = await api.getLead(leadId);
      setLead(updatedLead);
      
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al crear negocio');
    }
  };

  const addServiceToDeal = (service) => {
    setDealForm(prev => ({
      ...prev,
      services: [...prev.services, { ...service, value: service.value || service.minPrice || 0, quantity: 1 }]
    }));
  };

  const updateServiceQuantity = (index, quantity) => {
    setDealForm(prev => ({
      ...prev,
      services: prev.services.map((s, i) => i === index ? { ...s, quantity: parseInt(quantity) || 1 } : s)
    }));
  };

  const removeServiceFromDeal = (index) => {
    setDealForm(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  // Modal helpers
  const openModal = (setModalState) => {
    setIsDrawerOpen(false);
    setTimeout(() => setModalState(true), 300);
  };

  const closeModal = (setModalState) => {
    setModalState(false);
    setTimeout(() => setIsDrawerOpen(true), 100);
  };

  // Format helpers
  const formatValue = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'decimal', minimumFractionDigits: 0,
    }).format(value || 0) + ' COP';
  };

  const formatValueShort = (value) => {
    if (value >= 1000000000) return `${(value / 1000000000).toFixed(1)}B COP`;
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M COP`;
    return formatValue(value);
  };

  // Format number as currency for display in input
  const formatNumberAsCurrency = (value) => {
    if (!value && value !== 0) return '';
    return new Intl.NumberFormat('es-CO').format(value);
  };

  // Parse currency string back to number
  const parseCurrencyToNumber = (str) => {
    if (!str) return 0;
    // Remove all non-digit characters except for decimal point
    const cleaned = str.replace(/[^\d]/g, '');
    return parseInt(cleaned) || 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // ==================== HANDLERS ====================

  // Activity handlers
  const handleAddActivity = async () => {
    if (!activityForm.description.trim()) {
      toast.error('La descripción es requerida');
      return;
    }
    try {
      const activity = await api.createActivity({
        leadId, type: activityForm.type, description: activityForm.description, activityDate: activityForm.activityDate,
      });
      setActivities([activity, ...activities]);
      setActivityForm({ description: '', type: 'note', activityDate: new Date().toISOString().split('T')[0] });
      closeModal(setShowActivityModal);
      toast.success('Actividad añadida');
    } catch (error) {
      toast.error('Error al añadir actividad');
    }
  };

  const handleEditActivity = async () => {
    if (!editingItem) return;
    try {
      await api.updateActivity(editingItem.id, {
        description: activityForm.description,
        type: activityForm.type,
        activityDate: activityForm.activityDate
      });
      setActivities(activities.map(a => a.id === editingItem.id ? { ...a, ...activityForm } : a));
      setEditingItem(null);
      setEditingType(null);
      closeModal(setShowActivityModal);
      toast.success('Actividad actualizada');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDeleteActivity = async () => {
    if (!deletingItem) return;
    try {
      await api.deleteActivity(deletingItem.id);
      setActivities(activities.filter(a => a.id !== deletingItem.id));
      setDeletingItem(null);
      setDeletingType(null);
      toast.success('Actividad eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // File handlers
  // Handler para convertir archivo a base64
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validar tamaño (max 10MB para MongoDB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('El archivo es demasiado grande. Máximo 10MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setFileForm({
        ...fileForm,
        name: fileForm.name || file.name,
        file: file,
        fileData: reader.result.split(',')[1], // Solo el base64 sin el prefijo
        fileType: file.type,
        type: 'upload'
      });
    };
    reader.onerror = () => {
      toast.error('Error al leer el archivo');
    };
    reader.readAsDataURL(file);
  };

  const handleAddFile = async () => {
    // Validación según el tipo
    if (fileForm.type === 'link') {
      if (!fileForm.name.trim() || !fileForm.url.trim()) {
        toast.error('Nombre y URL son requeridos');
        return;
      }
      try {
        const attachment = await api.createAttachment({
          leadId, name: fileForm.name, type: 'link', url: fileForm.url,
        });
        setAttachments([attachment, ...attachments]);
        setFileForm({ name: '', url: '', type: 'link', file: null, fileData: null, fileType: null });
        closeModal(setShowFileModal);
        toast.success('Enlace añadido');
      } catch (error) {
        toast.error('Error al añadir enlace');
      }
    } else {
      // Upload file
      if (!fileForm.name.trim() || !fileForm.fileData) {
        toast.error('Nombre y archivo son requeridos');
        return;
      }
      try {
        const attachment = await api.createAttachment({
          leadId, 
          name: fileForm.name, 
          type: 'file',
          fileData: fileForm.fileData,
          fileType: fileForm.fileType,
          size: fileForm.file?.size || 0
        });
        setAttachments([attachment, ...attachments]);
        setFileForm({ name: '', url: '', type: 'link', file: null, fileData: null, fileType: null });
        closeModal(setShowFileModal);
        toast.success('Archivo subido exitosamente');
      } catch (error) {
        toast.error('Error al subir archivo');
      }
    }
  };

  const handleEditFile = async () => {
    if (!editingItem) return;
    try {
      const updates = {
        name: fileForm.name,
        url: fileForm.url || ''
      };
      // Si hay un nuevo archivo, incluirlo
      if (fileForm.fileData && fileForm.type === 'upload') {
        updates.fileData = fileForm.fileData;
        updates.fileType = fileForm.fileType;
        updates.type = 'file';
      }
      await api.updateAttachment(editingItem.id, updates);
      setAttachments(attachments.map(a => a.id === editingItem.id ? { ...a, ...updates } : a));
      setEditingItem(null);
      setEditingType(null);
      closeModal(setShowFileModal);
      toast.success('Archivo actualizado');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  // Handle lead assignment
  const handleAssignLead = async (userId) => {
    setLoadingAssignment(true);
    try {
      const result = await api.assignLead(leadId, userId);
      if (result.success) {
        setLead(result.lead);
        const assigned = availableUsers.find(u => u.id === userId);
        setAssignedUser(assigned || null);
        
        if (userId) {
          toast.success(`Lead asignado a ${assigned?.name || 'comercial'}`);
        } else {
          toast.success('Asignación removida');
        }
      }
    } catch (error) {
      console.error('Error assigning lead:', error);
      toast.error(error.response?.data?.detail || 'Error al asignar lead');
    } finally {
      setLoadingAssignment(false);
    }
  };

  const handleDeleteFile = async () => {
    if (!deletingItem) return;
    try {
      await api.deleteAttachment(deletingItem.id);
      setAttachments(attachments.filter(a => a.id !== deletingItem.id));
      setDeletingItem(null);
      setDeletingType(null);
      toast.success('Archivo eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Meeting handlers
  const handleAddMeeting = async () => {
    if (!meetingForm.title.trim() || !meetingForm.date) {
      toast.error('Título y fecha son requeridos');
      return;
    }
    // Validación: si es presencial, requiere ubicación
    if (meetingForm.meetingType === 'presencial' && !meetingForm.location.trim()) {
      toast.error('Para reuniones presenciales, la ubicación es requerida');
      return;
    }
    try {
      // Parse additional attendees
      const attendeeEmails = meetingForm.additionalAttendees
        ? meetingForm.additionalAttendees.split(',').map(e => e.trim()).filter(e => e)
        : [];
      
      // Add lead email as primary attendee
      const allAttendees = lead?.email ? [lead.email, ...attendeeEmails] : attendeeEmails;
      
      const response = await api.createMeeting({
        leadId, 
        title: meetingForm.title, 
        date: meetingForm.date,
        duration: meetingForm.duration,
        meetingType: meetingForm.meetingType,
        location: meetingForm.location,
        videoLink: meetingForm.videoLink, 
        transcription: meetingForm.transcription,
        description: meetingForm.description,
        attendees: allAttendees,
      });
      setMeetings([response.meeting || response, ...meetings]);
      setMeetingForm({ title: '', date: '', duration: 30, meetingType: 'virtual', location: '', videoLink: '', transcription: '', description: '', additionalAttendees: '' });
      closeModal(setShowMeetingModal);
      
      // Show toast with calendar sync status
      if (response.calendarSynced) {
        toast.success('Reunión añadida y sincronizada con Google Calendar 📅');
      } else {
        toast.success('Reunión añadida correctamente ✓');
      }
    } catch (error) {
      toast.error('Error al añadir reunión');
    }
  };

  const handleEditMeeting = async () => {
    if (!editingItem) return;
    try {
      // Parse additional attendees
      const attendeeEmails = meetingForm.additionalAttendees
        ? meetingForm.additionalAttendees.split(',').map(e => e.trim()).filter(e => e)
        : [];
      
      // Add lead email as primary attendee
      const allAttendees = lead?.email ? [lead.email, ...attendeeEmails] : attendeeEmails;
      
      const updateData = {
        ...meetingForm,
        attendees: allAttendees,
      };
      
      const response = await api.updateMeeting(editingItem.id, updateData);
      setMeetings(meetings.map(m => m.id === editingItem.id ? { ...m, ...updateData } : m));
      setEditingItem(null);
      setEditingType(null);
      closeModal(setShowMeetingModal);
      
      if (response.calendarUpdated) {
        toast.success('Reunión actualizada y sincronizada con Google Calendar 📅');
      } else {
        toast.success('Reunión actualizada');
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDeleteMeeting = async () => {
    if (!deletingItem) return;
    try {
      const response = await api.deleteMeeting(deletingItem.id);
      setMeetings(meetings.filter(m => m.id !== deletingItem.id));
      setDeletingItem(null);
      setDeletingType(null);
      
      if (response.calendarDeleted) {
        toast.success('Reunión eliminada y removida de Google Calendar 📅');
      } else {
        toast.success('Reunión eliminada');
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Cargar historial de formularios
  const loadFormHistory = async () => {
    setLoadingFormHistory(true);
    try {
      const history = await api.getLeadFormHistory(leadId);
      setFormHistory(history);
      setShowFormHistoryModal(true);
    } catch (error) {
      toast.error('Error al cargar historial de formularios');
    } finally {
      setLoadingFormHistory(false);
    }
  };

  // Cargar vista consolidada por email
  const loadConsolidatedView = async () => {
    if (!lead?.email) {
      toast.error('Este lead no tiene correo electrónico');
      return;
    }
    setLoadingConsolidated(true);
    try {
      const data = await api.consolidateLeadsByEmail(lead.email);
      setConsolidatedData(data);
      setShowConsolidatedModal(true);
    } catch (error) {
      toast.error('Error al cargar vista consolidada');
    } finally {
      setLoadingConsolidated(false);
    }
  };

  // Alert handlers
  const handleAddAlert = async () => {
    if (!alertForm.title.trim() || !alertForm.date) {
      toast.error('Título y fecha son requeridos');
      return;
    }
    // Use noon as default time since alerts are date-only
    const alertDateTime = `${alertForm.date}T12:00:00`;
    try {
      const alert = await api.createAlert({
        leadId, title: alertForm.title, description: alertForm.description,
        date: alertDateTime, priority: alertForm.priority, isRead: false,
      });
      setAlerts([alert, ...alerts]);
      setAlertForm({ title: '', description: '', date: '', priority: 'medium', sendEmail: false });
      closeModal(setShowAlertModal);
      toast.success('Alerta añadida');
    } catch (error) {
      toast.error('Error al añadir alerta');
    }
  };

  const handleEditAlert = async () => {
    if (!editingItem) return;
    const alertDateTime = `${alertForm.date}T12:00:00`;
    try {
      await api.updateAlert(editingItem.id, {
        title: alertForm.title, description: alertForm.description,
        date: alertDateTime, priority: alertForm.priority,
      });
      setAlerts(alerts.map(a => a.id === editingItem.id ? { ...a, title: alertForm.title, description: alertForm.description, date: alertDateTime, priority: alertForm.priority } : a));
      setEditingItem(null);
      setEditingType(null);
      closeModal(setShowAlertModal);
      toast.success('Alerta actualizada');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDeleteAlert = async () => {
    if (!deletingItem) return;
    try {
      await api.deleteAlert(deletingItem.id);
      setAlerts(alerts.filter(a => a.id !== deletingItem.id));
      setDeletingItem(null);
      setDeletingType(null);
      toast.success('Alerta eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Open edit modal
  const openEditModal = (item, type) => {
    setEditingItem(item);
    setEditingType(type);
    
    if (type === 'activity') {
      setActivityForm({ description: item.description, type: item.type, activityDate: item.activityDate || (item.createdAt ? item.createdAt.split('T')[0] : new Date().toISOString().split('T')[0]) });
      openModal(setShowActivityModal);
    } else if (type === 'file') {
      setFileForm({ name: item.name, url: item.url || '', type: item.fileData ? 'upload' : 'link', file: null, fileData: item.fileData || null, fileType: item.fileType || null });
      openModal(setShowFileModal);
    } else if (type === 'meeting') {
      // Extract additional attendees (excluding lead email)
      const attendees = item.attendees || [];
      const additionalAttendees = lead?.email 
        ? attendees.filter(e => e !== lead.email).join(', ')
        : attendees.join(', ');
      
      setMeetingForm({
        title: item.title,
        date: item.date || '',
        duration: item.duration || 30,
        meetingType: item.meetingType || 'virtual',
        location: item.location || '',
        videoLink: item.videoLink || '',
        transcription: item.transcription || '',
        description: item.description || '',
        additionalAttendees: additionalAttendees
      });
      openModal(setShowMeetingModal);
    } else if (type === 'alert') {
      setAlertForm({
        title: item.title,
        description: item.description || '',
        date: item.date?.split('T')[0] || '',
        priority: item.priority || 'medium',
        sendEmail: false
      });
      openModal(setShowAlertModal);
    }
  };

  // Open delete confirmation
  const openDeleteConfirm = (item, type) => {
    setIsDrawerOpen(false);
    setTimeout(() => {
      setDeletingItem(item);
      setDeletingType(type);
    }, 300);
  };

  // Handle delete confirm
  const handleDeleteConfirm = () => {
    if (deletingType === 'activity') handleDeleteActivity();
    else if (deletingType === 'file') handleDeleteFile();
    else if (deletingType === 'meeting') handleDeleteMeeting();
    else if (deletingType === 'alert') handleDeleteAlert();
  };

  // Lead delete handler
  const handleDeleteLead = async () => {
    try {
      await actions.deleteLead(leadId);
      toast.success(`Lead "${lead.name}" eliminado exitosamente`);
      navigate('/leads');
    } catch (error) {
      // Show detailed error message from backend
      const errorMessage = error.response?.data?.detail || 'Error al eliminar el lead';
      toast.error(errorMessage);
    }
  };

  if (loadingLead) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Lead no encontrado</h2>
          <button onClick={() => navigate('/leads')} className="text-indigo-600 hover:text-indigo-700 font-medium">
            Volver a Leads
          </button>
        </div>
      </div>
    );
  }

  return (
    <Drawer.Root direction="right" open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
      <div className="h-screen flex flex-col bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-slate-900">{lead.name}</h1>
                {lead.code && (
                  <span className="px-2.5 py-1 bg-indigo-100 text-indigo-700 text-sm font-mono font-semibold rounded-lg">
                    {lead.code}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-sm text-slate-500">{pipeline?.name}</span>
                <span className="text-slate-300">•</span>
                <span className="text-sm px-2 py-1 rounded-full font-medium"
                  style={{ backgroundColor: `${stage?.color}20`, color: stage?.color }}>
                  {stage?.name}
                </span>
                {lead.lossReason && (
                  <span className="text-sm px-2 py-1 rounded-full font-medium bg-red-50 text-red-600 border border-red-200">
                    Motivo: {{
                      'no_response': 'Cliente no responde',
                      'incomplete_docs': 'Documentación incompleta',
                      'cheaper_price': 'Precio de la oferta',
                      'project_cancelled': 'No van a continuar con el proyecto',
                      'no_budget': 'No tienen presupuesto',
                      'internal_solution': 'Van a hacerlo internamente',
                      'other_provider': 'Se fueron con otro proveedor',
                      'spam': 'Spam',
                      'business_partner': 'Buscaban Socios Comercial',
                      'internal_test': 'Prueba interna',
                      'job_seeker': 'Busca empleo',
                    }[lead.lossReason] || lead.lossReason}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Ver vista consolidada por email */}
              {/* Ver historial de formularios */}
              <button 
                onClick={loadFormHistory}
                disabled={loadingFormHistory}
                className="flex items-center gap-2 px-4 py-2 text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors font-medium"
                title="Ver historial de respuestas de formularios"
              >
                <Eye className="w-4 h-4" />
                {loadingFormHistory ? 'Cargando...' : 'Historial'}
              </button>
              {/* Crear Negocio button - only show if lead has an associated empresa and no existing deals */}
              {lead?.empresaId && empresaAsociada && (
                leadJourney?.deals?.total > 0 ? (
                  <div className="flex items-center gap-2 px-4 py-2 text-slate-400 bg-slate-100 rounded-lg cursor-not-allowed font-medium" title="Este lead ya tiene un negocio asociado">
                    <Plus className="w-4 h-4" /> Negocio Existente
                  </div>
                ) : (
                  <button onClick={() => setShowDealModal(true)}
                    className="flex items-center gap-2 px-4 py-2 text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors font-medium">
                    <Plus className="w-4 h-4" /> Crear Negocio
                  </button>
                )
              )}
              {canEditLead && (
                <button onClick={() => { setIsDrawerOpen(false); setTimeout(() => setShowEditModal(true), 300); }}
                  className="flex items-center gap-2 px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors font-medium">
                  <Edit2 className="w-4 h-4" /> Editar
                </button>
              )}
              {/* Delete Lead button - only for admins */}
              {isAdmin && (
                <button 
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center gap-2 px-4 py-2 text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors font-medium"
                  title="Eliminar lead (solo si no tiene negocios ni empresa asociada)"
                >
                  <Trash2 className="w-4 h-4" /> Eliminar
                </button>
              )}
              <Drawer.Trigger asChild>
                <button onClick={() => setIsDrawerOpen(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm font-medium">
                  Ver Detalles Completos
                </button>
              </Drawer.Trigger>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto p-8">
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Empresa Asociada Card - Full Width, Prominent */}
            <div className="md:col-span-2">
              {empresaAsociada ? (
                <div 
                  onClick={() => navigate(`/empresa/${empresaAsociada.id}`)}
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-5 shadow-lg cursor-pointer hover:shadow-xl transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center text-white text-2xl font-bold">
                        {empresaAsociada.name?.charAt(0) || 'E'}
                      </div>
                      <div className="text-white">
                        <p className="text-xs font-medium opacity-80 mb-1">EMPRESA ASOCIADA</p>
                        <h3 className="text-xl font-bold">{empresaAsociada.name}</h3>
                        <p className="text-sm opacity-80">
                          {empresaAsociada.documentType}: {empresaAsociada.documentNumber} · {empresaAsociada.personType}
                        </p>
                      </div>
                    </div>
                    <div className="text-white text-right">
                      <p className="text-sm opacity-80">Ver empresa →</p>
                      <p className="text-xs opacity-60 group-hover:opacity-100 transition-opacity">Click para ver detalles y negocios</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-amber-800">Sin Empresa Asociada</p>
                      <p className="text-xs text-amber-700">
                        Este lead no está asociado a ninguna empresa. Para crear negocios, primero debe crear una empresa y asociar este lead.
                      </p>
                    </div>
                    <button 
                      onClick={() => navigate('/empresas')}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
                    >
                      Ir a Empresas
                    </button>
                  </div>
                </div>
              )}            </div>

            {/* Comercial + Referidor - Side by Side (hidden for trafficker) */}
            {!isTrafficker && (
            <div className="md:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Comercial Asignado */}
                <div className={`rounded-xl p-4 border ${assignedUser ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                  <p className="text-xs font-medium text-slate-500 uppercase mb-3">Comercial Asignado</p>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${assignedUser ? 'bg-green-200 text-green-700' : 'bg-slate-200 text-slate-500'}`}>
                      {assignedUser ? (
                        <span className="font-bold text-sm">{assignedUser.name?.charAt(0) || 'U'}</span>
                      ) : (
                        <UserCog className="w-4 h-4" />
                      )}
                    </div>
                    <p className="font-semibold text-slate-900 text-sm">{assignedUser?.name || 'Sin asignar'}</p>
                  </div>
                  <div>
                    {loadingAssignment ? (
                      <span className="text-sm text-slate-500">Guardando...</span>
                    ) : isAdmin ? (
                      <select
                        value={lead?.assignedTo || ''}
                        onChange={(e) => handleAssignLead(e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500"
                        data-testid="assign-lead-select"
                      >
                        <option value="">Sin asignar</option>
                        {availableUsers.filter(u => u.role !== 'referidor').map(user => (
                          <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                      </select>
                    ) : !assignedUser ? (
                      <button
                        onClick={() => handleAssignLead(currentUser.id)}
                        className="w-full px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        data-testid="self-assign-btn"
                      >
                        Asignarme este lead
                      </button>
                    ) : null}
                    {lead?.assignedAt && (
                      <p className="text-xs text-slate-400 mt-2">
                        Asignado el {new Date(lead.assignedAt).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>

                {/* Referidor */}
                {isAdmin && (
                <div className={`rounded-xl p-4 border ${lead?.referidorId ? 'bg-purple-50 border-purple-200' : 'bg-slate-50 border-slate-200'}`} data-testid="referidor-assignment-card">
                  <p className="text-xs font-medium text-slate-500 uppercase mb-3">Referidor</p>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${lead?.referidorId ? 'bg-purple-200 text-purple-700' : 'bg-slate-200 text-slate-500'}`}>
                      {lead?.referidorId ? (
                        <span className="font-bold text-sm">{(lead?.referidorName || '?').charAt(0).toUpperCase()}</span>
                      ) : (
                        <Users className="w-4 h-4" />
                      )}
                    </div>
                    <p className="font-semibold text-slate-900 text-sm">{lead?.referidorName || 'DataLab'}</p>
                  </div>
                  <select
                    value={lead?.referidorId || ''}
                    onChange={async (e) => {
                      try {
                        await api.assignReferidorToLead(lead.id, e.target.value);
                        const selectedRef = availableUsers.find(u => u.id === e.target.value);
                        setLead(prev => ({
                          ...prev,
                          referidorId: e.target.value || undefined,
                          referidorName: selectedRef?.name || '',
                          referidorEmail: selectedRef?.email || ''
                        }));
                        toast.success(e.target.value ? 'Referidor asignado' : 'Referidor removido');
                      } catch (error) {
                        toast.error('Error al asignar referidor');
                      }
                    }}
                    className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
                    data-testid="assign-referidor-select"
                  >
                    <option value="">DataLab (Sin referidor)</option>
                    {availableUsers.filter(u => u.role === 'referidor').map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                </div>
                )}
              </div>
            </div>
            )}

            {/* Lead Info Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900">Información del Contacto</h2>
                {lead.phone && <WhatsAppButton lead={lead} />}
              </div>
              <div className="space-y-3">
                {lead.email && <div><p className="text-xs text-slate-500">Email</p><p className="font-medium text-slate-900">{lead.email}</p></div>}
                {lead.phone && <div><p className="text-xs text-slate-500">Teléfono</p><p className="font-medium text-slate-900">{lead.phone}</p></div>}
                
                {/* Location Information */}
                {(lead.country || lead.city) && (
                  <div className="grid grid-cols-2 gap-3">
                    {lead.country && (
                      <div>
                        <p className="text-xs text-slate-500">País</p>
                        <p className="font-medium text-slate-900">{lead.country}</p>
                      </div>
                    )}
                    {lead.city && (
                      <div>
                        <p className="text-xs text-slate-500">Ciudad</p>
                        <p className="font-medium text-slate-900">{lead.city}</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Document Information */}
                {lead.documentType && (
                  <div className="pt-2 border-t border-slate-100">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-slate-500">Tipo Documento</p>
                        <p className="font-medium text-slate-900">{lead.documentType}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Número Documento</p>
                        <p className="font-medium text-slate-900">{lead.documentNumber}</p>
                      </div>
                    </div>
                    {lead.personType && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500">Tipo Persona</p>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          lead.personType === 'Persona Jurídica' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                        }`}>{lead.personType}</span>
                      </div>
                    )}
                  </div>
                )}
                
                <div><p className="text-xs text-slate-500">Valor Negocios</p><p className="font-medium text-green-600 text-lg">{formatValue(lead.dealsTotalValue || 0)}</p></div>
                <div><p className="text-xs text-slate-500">Canal de Origen</p><p className="font-medium text-slate-900">{lead.trackingData?.channel || lead.source || 'Manual'}</p></div>
                
                {/* Campaña Section - Editable for trafficker */}
                <div className="col-span-2 pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-500 mb-2">Campaña</p>
                  <div className="flex flex-wrap gap-2">
                    {/* Current campaign tags */}
                    {lead.campaignTags && lead.campaignTags.map((ct, idx) => (
                      <div
                        key={ct.tag || idx}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border bg-indigo-100 text-indigo-700 border-indigo-200"
                      >
                        <Tag className="w-3.5 h-3.5" />
                        <span className="font-medium">{ct.name || ct.tag}</span>
                        {ct.addedAt && (
                          <span className="text-xs opacity-75 ml-1 border-l pl-1.5 border-current/20">
                            {new Date(ct.addedAt).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}
                          </span>
                        )}
                        {(isTrafficker || canEditLead) && (
                          <button
                            onClick={async () => {
                              try {
                                await api.removeCampaignFromLead(lead.id, ct.tag);
                                setLead(prev => ({ ...prev, campaignTags: prev.campaignTags.filter(t => t.tag !== ct.tag) }));
                                toast.success('Campaña removida');
                              } catch { toast.error('Error al remover campaña'); }
                            }}
                            className="ml-1 p-0.5 hover:bg-black/10 rounded-full"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                    {/* Old format fallback */}
                    {lead.campaignTag && (!lead.campaignTags || lead.campaignTags.length === 0) && (
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border bg-indigo-100 text-indigo-700 border-indigo-200">
                        <Tag className="w-3.5 h-3.5" />
                        <span className="font-medium">{lead.campaignTag}</span>
                      </div>
                    )}
                  </div>
                  {/* Campaign change selector for trafficker (only when no campaign assigned, since lead = 1 campaign) */}
                  {(isTrafficker || canEditLead) && (!lead.campaignTags || lead.campaignTags.length === 0) && (
                    <select
                      className="mt-2 w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg"
                      defaultValue=""
                      onChange={async (e) => {
                        const tag = e.target.value;
                        if (!tag) return;
                        try {
                          const result = await api.addCampaignToLead(lead.id, tag);
                          setLead(prev => ({ ...prev, campaignTags: result.campaignTags || [{ tag, addedAt: new Date().toISOString() }] }));
                          toast.success('Campaña asignada');
                        } catch { toast.error('Error al asignar campaña'); }
                        e.target.value = '';
                      }}
                      data-testid="trafficker-campaign-selector"
                    >
                      <option value="">Asignar campaña...</option>
                      {(availableCampaigns || []).map(c => (
                        <option key={c.tag} value={c.tag}>{c.name} ({c.channel})</option>
                      ))}
                    </select>
                  )}
                </div>
                
                {/* Lead Tags Section */}
                <LeadTagsWidget lead={lead} setLead={setLead} canEdit={canEditLead} />
                
                <div><p className="text-xs text-slate-500">Prioridad</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                    lead.priority === 'Alta' ? 'bg-red-100 text-red-700' :
                    lead.priority === 'Baja' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                  }`}>{lead.priority || 'Media'}</span>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Calificación</p>
                  <StarRating 
                    rating={lead.rating || 0} 
                    onRate={async (rating) => {
                      try {
                        await api.updateLeadRating(lead.id, rating);
                        setLead(prev => ({ ...prev, rating }));
                        toast.success('Calificación actualizada');
                      } catch (error) {
                        toast.error('Error al actualizar calificación');
                      }
                    }}
                    size="md"
                  />
                </div>
              </div>
            </div>

            {/* Stats Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Resumen</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{activities.length}</p>
                  <p className="text-sm text-slate-500">Actividades</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{attachments.length}</p>
                  <p className="text-sm text-slate-500">Archivos</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{meetings.length}</p>
                  <p className="text-sm text-slate-500">Reuniones</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{alerts.length}</p>
                  <p className="text-sm text-slate-500">Alertas</p>
                </div>
              </div>
            </div>

            {/* Business Metrics Card - Compact version for main view */}
            {leadJourney && (leadJourney.deals?.total > 0 || leadJourney.services?.totalContracted > 0) && (
              <div className="md:col-span-2 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-xl border border-indigo-100 shadow-sm p-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  📊 Métricas de Negocios
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/80 backdrop-blur rounded-xl p-4 text-center shadow-sm">
                    <p className="text-3xl font-bold text-blue-600">{leadJourney.deals?.total || 0}</p>
                    <p className="text-sm text-slate-600 font-medium">Negocios</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur rounded-xl p-4 text-center shadow-sm">
                    <p className="text-3xl font-bold text-green-600">{leadJourney.deals?.won || 0}</p>
                    <p className="text-sm text-slate-600 font-medium">Ganados ✓</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur rounded-xl p-4 text-center shadow-sm">
                    <p className="text-2xl font-bold text-emerald-600">{formatValueShort(leadJourney.deals?.totalWonValue || 0)}</p>
                    <p className="text-sm text-slate-600 font-medium">Valor Ganado</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur rounded-xl p-4 text-center shadow-sm">
                    <p className="text-3xl font-bold text-purple-600">{leadJourney.services?.totalContracted || 0}</p>
                    <p className="text-sm text-slate-600 font-medium">Servicios</p>
                  </div>
                </div>
                
                {/* Conversion rate and avg deal value */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl p-4 text-white">
                    <p className="text-xs font-medium opacity-90">🎯 Tasa Conversión</p>
                    <p className="text-2xl font-bold">{(leadJourney.deals?.conversionRate || 0).toFixed(1)}%</p>
                  </div>
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl p-4 text-white">
                    <p className="text-xs font-medium opacity-90">📈 Promedio por Negocio</p>
                    <p className="text-2xl font-bold">{formatValueShort(leadJourney.deals?.avgDealValue || 0)}</p>
                  </div>
                </div>
                
                {/* Top services mini */}
                {leadJourney.services?.topServices?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-indigo-100">
                    <p className="text-sm font-semibold text-slate-700 mb-2">🛍️ Top Servicios Contratados:</p>
                    <div className="flex flex-wrap gap-2">
                      {leadJourney.services.topServices.slice(0, 3).map((service, idx) => (
                        <span key={idx} className="px-3 py-1 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200">
                          {service.name}: {formatValueShort(service.totalValue)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Lead Journey Card - Full Width */}
            <div className="md:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Proceso en el Embudo
              </h2>
              
              {loadingJourney ? (
                <div className="text-center py-8 text-slate-500">Cargando historial...</div>
              ) : leadJourney ? (
                <div className="space-y-6">
                  {/* Pipeline Info */}
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-500">Embudo</p>
                      <p className="font-semibold text-slate-900">{leadJourney.pipeline?.name || 'N/A'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Días en proceso</p>
                      <p className="text-2xl font-bold text-indigo-600">{leadJourney.daysInPipeline}</p>
                    </div>
                  </div>

                  {/* Current Stage Indicator */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Etapa Actual</h3>
                    <div className="flex items-center gap-1">
                      {leadJourney.pipeline?.stages?.map((stageItem, idx) => {
                        const isCurrent = lead.stageId === stageItem.id;
                        return (
                          <div key={stageItem.id} className="flex-1 flex flex-col items-center">
                            <div
                              className={`w-full h-2 rounded-full ${
                                isCurrent ? 'bg-indigo-500' : 'bg-slate-200'
                              }`}
                            />
                            <span className={`text-xs mt-1 text-center ${
                              isCurrent ? 'text-indigo-600 font-semibold' : 'text-slate-400'
                            }`}>
                              {stageItem.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Lead Individual Statistics */}
                  {leadJourney.statistics && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 mb-3">Comportamiento del Lead</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className={`text-center p-3 rounded-lg ${
                          leadJourney.statistics.daysSinceContact > 30 ? 'bg-red-50' :
                          leadJourney.statistics.daysSinceContact > 7 ? 'bg-yellow-50' : 'bg-green-50'
                        }`}>
                          <p className={`text-xl font-bold ${
                            leadJourney.statistics.daysSinceContact > 30 ? 'text-red-600' :
                            leadJourney.statistics.daysSinceContact > 7 ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {leadJourney.statistics.daysSinceContact}d
                          </p>
                          <p className="text-xs text-slate-600">Sin contacto</p>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <p className="text-xl font-bold text-blue-600">{leadJourney.statistics.engagementScore}%</p>
                          <p className="text-xs text-slate-600">Engagement</p>
                        </div>
                        <div className="text-center p-3 bg-purple-50 rounded-lg">
                          <p className="text-xl font-bold text-purple-600">{leadJourney.statistics.responseRate}%</p>
                          <p className="text-xs text-slate-600">Tasa Respuesta</p>
                        </div>
                        <div className={`text-center p-3 rounded-lg ${
                          leadJourney.statistics.riskLevel === 'alto' ? 'bg-red-50' :
                          leadJourney.statistics.riskLevel === 'medio' ? 'bg-yellow-50' : 'bg-green-50'
                        }`}>
                          <p className={`text-xl font-bold capitalize ${
                            leadJourney.statistics.riskLevel === 'alto' ? 'text-red-600' :
                            leadJourney.statistics.riskLevel === 'medio' ? 'text-yellow-600' : 'text-green-600'
                          }`}>
                            {leadJourney.statistics.riskLevel}
                          </p>
                          <p className="text-xs text-slate-600">Nivel Riesgo</p>
                        </div>
                      </div>
                      
                      {/* Activity counts */}
                      <div className="mt-3 grid grid-cols-4 gap-2">
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-sm font-bold text-slate-700">{leadJourney.statistics.activityCounts?.llamadas || 0}</p>
                          <p className="text-xs text-slate-500">Llamadas</p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-sm font-bold text-slate-700">{leadJourney.statistics.activityCounts?.emails || 0}</p>
                          <p className="text-xs text-slate-500">Emails</p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-sm font-bold text-slate-700">{leadJourney.statistics.activityCounts?.reuniones || 0}</p>
                          <p className="text-xs text-slate-500">Reuniones</p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-sm font-bold text-slate-700">{leadJourney.statistics.activityCounts?.notas || 0}</p>
                          <p className="text-xs text-slate-500">Notas</p>
                        </div>
                      </div>
                      
                      {/* Recommended action */}
                      {leadJourney.statistics.nextActionRecommended && (
                        <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                          <p className="text-xs text-indigo-600 font-medium">Acción Recomendada</p>
                          <p className="text-sm font-semibold text-indigo-800">{leadJourney.statistics.nextActionRecommended}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Deals Summary with Enhanced Metrics */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">📊 Métricas de Negocios</h3>
                    {leadJourney.deals?.total > 0 ? (
                      <div className="space-y-4">
                        {/* Main KPIs */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                            <p className="text-2xl font-bold text-blue-600">{leadJourney.deals.total}</p>
                            <p className="text-xs text-blue-700 font-medium">Negocios Totales</p>
                          </div>
                          <div className="text-center p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-100">
                            <p className="text-2xl font-bold text-green-600">{leadJourney.deals.won}</p>
                            <p className="text-xs text-green-700 font-medium">Ganados ✓</p>
                          </div>
                          <div className="text-center p-3 bg-gradient-to-br from-red-50 to-rose-50 rounded-lg border border-red-100">
                            <p className="text-2xl font-bold text-red-600">{leadJourney.deals.lost}</p>
                            <p className="text-xs text-red-700 font-medium">Perdidos</p>
                          </div>
                          <div className="text-center p-3 bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg border border-orange-100">
                            <p className="text-2xl font-bold text-orange-600">{leadJourney.deals.inProgress || 0}</p>
                            <p className="text-xs text-orange-700 font-medium">En Proceso</p>
                          </div>
                        </div>
                        
                        {/* Value Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="p-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl text-white">
                            <p className="text-xs font-medium opacity-90">💰 Valor Total Ganado</p>
                            <p className="text-2xl font-bold">{formatValueShort(leadJourney.deals.totalWonValue || 0)}</p>
                          </div>
                          <div className="p-4 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl text-white">
                            <p className="text-xs font-medium opacity-90">📈 Promedio por Negocio</p>
                            <p className="text-2xl font-bold">{formatValueShort(leadJourney.deals.avgDealValue || 0)}</p>
                          </div>
                          <div className="p-4 bg-gradient-to-r from-purple-500 to-violet-500 rounded-xl text-white">
                            <p className="text-xs font-medium opacity-90">🎯 Tasa Conversión</p>
                            <p className="text-2xl font-bold">{(leadJourney.deals.conversionRate || 0).toFixed(1)}%</p>
                          </div>
                        </div>

                        {/* Services Contracted */}
                        {leadJourney.services && leadJourney.services.totalContracted > 0 && (
                          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-semibold text-slate-700">🛍️ Servicios Contratados</h4>
                              <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full">
                                {leadJourney.services.totalContracted} unidades
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 mb-3">
                              <div className="text-center p-2 bg-white rounded-lg">
                                <p className="text-lg font-bold text-indigo-600">{leadJourney.services.uniqueServices}</p>
                                <p className="text-xs text-slate-600">Servicios Únicos</p>
                              </div>
                              <div className="text-center p-2 bg-white rounded-lg">
                                <p className="text-lg font-bold text-green-600">{formatValueShort(leadJourney.services.totalValue || 0)}</p>
                                <p className="text-xs text-slate-600">Valor Total Servicios</p>
                              </div>
                            </div>
                            
                            {/* Top Services */}
                            {leadJourney.services.topServices?.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-slate-500">Top Servicios:</p>
                                {leadJourney.services.topServices.map((service, idx) => (
                                  <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg">
                                    <div className="flex items-center gap-2">
                                      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                                        idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                                        idx === 1 ? 'bg-slate-200 text-slate-700' :
                                        idx === 2 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'
                                      }`}>
                                        {idx + 1}
                                      </span>
                                      <span className="text-sm font-medium text-slate-900">{service.name}</span>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-semibold text-green-600">{formatValueShort(service.totalValue)}</p>
                                      <p className="text-xs text-slate-500">{service.totalQuantity} unidades</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Deal List */}
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-slate-500 mb-2">Últimos Negocios:</p>
                          {leadJourney.deals.items?.slice(0, 5).map((deal, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100 hover:border-slate-300 transition-colors">
                              <div>
                                <div className="flex items-center gap-2">
                                  {deal.code && (
                                    <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded font-semibold">{deal.code}</span>
                                  )}
                                  <p className="font-medium text-slate-900">{deal.leadName || deal.company || 'Negocio'}</p>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-slate-500">🛒 {deal.services?.length || 0} servicios</span>
                                  {deal.actualCloseDate && (
                                    <span className="text-xs text-slate-400">
                                      📅 {new Date(deal.actualCloseDate).toLocaleDateString('es-CO')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-slate-900">{formatValueShort(deal.totalValue)}</p>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  deal.status === 'won' ? 'bg-green-100 text-green-700' :
                                  deal.status === 'lost' ? 'bg-red-100 text-red-700' :
                                  'bg-orange-100 text-orange-700'
                                }`}>
                                  {deal.status === 'won' ? '✓ Ganado' : deal.status === 'lost' ? '✗ Perdido' : '⏳ Negociación'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                        <div className="text-4xl mb-2">📭</div>
                        <p className="text-slate-500 font-medium">Este lead aún no tiene negocios asociados</p>
                        <p className="text-xs text-slate-400 mt-1">Los negocios aparecerán aquí cuando se creen</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">No se pudo cargar el historial</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Drawer */}
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/40 z-40 pointer-events-none" />
        <Drawer.Content className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-white z-50 shadow-xl overflow-hidden flex flex-col pointer-events-auto">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <Drawer.Title className="text-2xl font-bold text-slate-900">Detalles del Lead</Drawer.Title>
              <Drawer.Close asChild>
                <button className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
              </Drawer.Close>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-6">
            <Tabs defaultValue="activity" className="w-full">
              <TabsList className="grid w-full grid-cols-5 mb-6">
                <TabsTrigger value="activity">Actividad</TabsTrigger>
                <TabsTrigger value="files">Archivos</TabsTrigger>
                <TabsTrigger value="meetings">Reuniones</TabsTrigger>
                <TabsTrigger value="alerts">Alertas</TabsTrigger>
                <TabsTrigger value="tracking">Tracking</TabsTrigger>
              </TabsList>

              {/* Activities Tab */}
              <TabsContent value="activity" className="space-y-4">
                {/* Tab Description */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-900 mb-1">📝 Registro de Actividades</h4>
                  <p className="text-sm text-blue-700">Documenta todas las interacciones con el lead: llamadas, emails, notas importantes. Esto te ayuda a tener un historial completo y no perder contexto en el seguimiento.</p>
                  <p className="text-xs text-blue-600 mt-2"><strong>Recomendación:</strong> Registra cada interacción inmediatamente después de realizarla.</p>
                </div>
                <button onClick={() => { setEditingItem(null); setEditingType(null); setActivityForm({ description: '', type: 'note', activityDate: new Date().toISOString().split('T')[0] }); openModal(setShowActivityModal); }}
                  className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors">
                  <Plus className="w-4 h-4" /> Añadir Actividad
                </button>
                {loadingDetails ? (
                  <div className="text-center py-8 text-slate-500">Cargando...</div>
                ) : activities.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No hay actividades</p>
                ) : (
                  <div className="space-y-3">
                    {activities.slice(0, 20).map((activity) => (
                      <div key={activity.id} className={`rounded-lg p-4 group ${activity.isReferidorNote ? 'bg-purple-50 border border-purple-200' : 'bg-slate-50'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                activity.isReferidorNote ? 'bg-purple-200 text-purple-800' :
                                activity.type === 'call' ? 'bg-blue-100 text-blue-700' :
                                activity.type === 'email' ? 'bg-purple-100 text-purple-700' :
                                activity.type === 'meeting' ? 'bg-green-100 text-green-700' :
                                'bg-slate-200 text-slate-700'
                              }`}>
                                {activity.isReferidorNote ? 'Nota del Referidor' : activity.type}
                              </span>
                              {activity.title && (
                                <span className="text-sm font-medium text-slate-700">{activity.title}</span>
                              )}
                              {activity.createdBy && activity.isReferidorNote && (
                                <span className="text-xs text-purple-600">por {activity.createdBy}</span>
                              )}
                            </div>
                            <p className="text-slate-900 mt-2">{activity.content || activity.description}</p>
                            <p className="text-xs text-slate-500 mt-1">{formatDate(activity.createdAt)}</p>
                          </div>
                          {!activity.isReferidorNote && (
                            <div className="flex gap-1">
                              <button onClick={() => openEditModal(activity, 'activity')} className="p-1 hover:bg-white rounded"><Edit2 className="w-4 h-4 text-slate-400 hover:text-indigo-600" /></button>
                              <button onClick={() => openDeleteConfirm(activity, 'activity')} className="p-1 hover:bg-white rounded"><Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" /></button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {activities.length > 20 && <p className="text-center text-slate-500 text-sm">+{activities.length - 20} más</p>}
                  </div>
                )}
              </TabsContent>

              {/* Files Tab */}
              <TabsContent value="files" className="space-y-4">
                {/* Tab Description */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-purple-900 mb-1">📎 Documentos y Enlaces</h4>
                  <p className="text-sm text-purple-700">Almacena documentos importantes relacionados con el lead: propuestas, cotizaciones, contratos, presentaciones o cualquier archivo relevante.</p>
                  <p className="text-xs text-purple-600 mt-2"><strong>Formatos soportados:</strong> PDF, imágenes (JPG, PNG), Word, Excel, PowerPoint, archivos comprimidos (ZIP).</p>
                  <p className="text-xs text-purple-600"><strong>Recomendación:</strong> Mantén los documentos organizados con nombres descriptivos.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => { setEditingItem(null); setEditingType(null); setFileForm({ name: '', url: '', type: 'link' }); openModal(setShowFileModal); }}
                    className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors">
                    <LinkIcon className="w-4 h-4" /> Agregar Enlace
                  </button>
                  <button onClick={() => { setEditingItem(null); setEditingType(null); setFileForm({ name: '', url: '', type: 'upload' }); openModal(setShowFileModal); }}
                    className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg text-slate-500 hover:border-green-300 hover:text-green-600 transition-colors">
                    <Upload className="w-4 h-4" /> Subir Archivo
                  </button>
                </div>
                {loadingDetails ? (
                  <div className="text-center py-8 text-slate-500">Cargando...</div>
                ) : attachments.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No hay archivos</p>
                ) : (
                  <div className="space-y-3">
                    {attachments.slice(0, 20).map((file) => (
                      <div key={file.id} className="bg-slate-50 rounded-lg p-4 group">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`p-2 rounded-lg ${file.isReferidorDocument ? 'bg-purple-100' : file.fileData ? 'bg-green-100' : 'bg-indigo-100'}`}>
                              {file.fileData ? <FileText className={`w-4 h-4 ${file.isReferidorDocument ? 'text-purple-600' : 'text-green-600'}`} /> : <LinkIcon className="w-4 h-4 text-indigo-600" />}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-900">
                                  {file.title || file.name}
                                </p>
                                {file.isReferidorDocument && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                                    Cargado por Referidor
                                  </span>
                                )}
                              </div>
                              {file.title && file.name && file.title !== file.name && (
                                <p className="text-xs text-slate-500">{file.name}</p>
                              )}
                              <div className="flex items-center gap-2">
                                {file.fileData ? (
                                  <>
                                    <span className="text-xs text-slate-500">{file.fileType || 'Archivo'}</span>
                                    {file.size && <span className="text-xs text-slate-400">• {(file.size / 1024).toFixed(1)} KB</span>}
                                    <a 
                                      href={`data:${file.fileType};base64,${file.fileData}`} 
                                      download={file.name}
                                      className="text-xs text-green-600 hover:underline flex items-center gap-1"
                                    >
                                      <Upload className="w-3 h-3 rotate-180" /> Descargar
                                    </a>
                                  </>
                                ) : (
                                  file.url && <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline">Ver enlace ↗</a>
                                )}
                              </div>
                            </div>
                          </div>
                          {!file.isReferidorDocument && (
                            <div className="flex gap-1">
                              <button onClick={() => openEditModal(file, 'file')} className="p-1 hover:bg-white rounded"><Edit2 className="w-4 h-4 text-slate-400 hover:text-indigo-600" /></button>
                              <button onClick={() => openDeleteConfirm(file, 'file')} className="p-1 hover:bg-white rounded"><Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" /></button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {attachments.length > 20 && <p className="text-center text-slate-500 text-sm">+{attachments.length - 20} más</p>}
                  </div>
                )}
              </TabsContent>

              {/* Meetings Tab */}
              <TabsContent value="meetings" className="space-y-4">
                {/* Tab Description */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-green-900 mb-1">📅 Gestión de Reuniones</h4>
                  <p className="text-sm text-green-700">Programa y registra reuniones con el lead. Puedes guardar enlaces a videollamadas, notas de la reunión y transcripciones.</p>
                  <p className="text-xs text-green-600 mt-2"><strong>Tipos:</strong> Presencial (en oficina/lugar físico) o Virtual (videollamada).</p>
                  <p className="text-xs text-green-600"><strong>Recomendación:</strong> Siempre registra un resumen después de cada reunión.</p>
                </div>
                <button onClick={() => { setEditingItem(null); setEditingType(null); setMeetingForm({ title: '', date: '', duration: 30, meetingType: 'virtual', location: '', videoLink: '', transcription: '', description: '', additionalAttendees: '' }); openModal(setShowMeetingModal); }}
                  className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors">
                  <Plus className="w-4 h-4" /> Añadir Reunión
                </button>
                {loadingDetails ? (
                  <div className="text-center py-8 text-slate-500">Cargando...</div>
                ) : meetings.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No hay reuniones</p>
                ) : (
                  <div className="space-y-3">
                    {meetings.slice(0, 20).map((meeting) => (
                      <div key={meeting.id} className="bg-slate-50 rounded-lg p-4 group">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${meeting.meetingType === 'presencial' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                {meeting.meetingType === 'presencial' ? '📍 Presencial' : '💻 Virtual'}
                              </span>
                              <p className="font-medium text-slate-900">{meeting.title}</p>
                              {meeting.googleCalendarEventId && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">📅 Calendar</span>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 mt-1"><Calendar className="w-3 h-3 inline mr-1" />{formatDate(meeting.date)}</p>
                            {meeting.location && <p className="text-xs text-slate-500">📍 {meeting.location}</p>}
                            {(meeting.meetLink || meeting.videoLink) && (
                              <a href={meeting.meetLink || meeting.videoLink} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline flex items-center gap-1 mt-1">
                                <Video className="w-3 h-3" />{meeting.meetLink ? 'Unirse a Google Meet' : 'Ver grabación'}
                              </a>
                            )}
                            
                            {/* Attendee Status - Auto synced */}
                            {meeting.attendeeStatus && meeting.attendeeStatus.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-slate-200">
                                <p className="text-xs font-medium text-slate-600 mb-1">Asistentes:</p>
                                <div className="flex flex-wrap gap-1">
                                  {meeting.attendeeStatus.map((attendee, idx) => (
                                    <span 
                                      key={idx}
                                      className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                                        attendee.responseStatus === 'accepted' ? 'bg-green-100 text-green-700' :
                                        attendee.responseStatus === 'declined' ? 'bg-red-100 text-red-700' :
                                        attendee.responseStatus === 'tentative' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-slate-100 text-slate-600'
                                      }`}
                                      title={`${attendee.email} - ${
                                        attendee.responseStatus === 'accepted' ? 'Aceptó' :
                                        attendee.responseStatus === 'declined' ? 'Rechazó' :
                                        attendee.responseStatus === 'tentative' ? 'Tentativo' : 'Sin respuesta'
                                      }`}
                                    >
                                      {attendee.responseStatus === 'accepted' ? '✓' :
                                       attendee.responseStatus === 'declined' ? '✗' :
                                       attendee.responseStatus === 'tentative' ? '?' : '○'}
                                      {attendee.displayName || attendee.email.split('@')[0]}
                                      {attendee.organizer && ' 👑'}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Additional attendees without status */}
                            {!meeting.attendeeStatus && meeting.additionalAttendees && meeting.additionalAttendees.length > 0 && (
                              <div className="mt-2 text-xs text-slate-500">
                                <span className="font-medium">Invitados:</span> {Array.isArray(meeting.additionalAttendees) ? meeting.additionalAttendees.join(', ') : String(meeting.additionalAttendees)}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => openEditModal(meeting, 'meeting')} className="p-1 hover:bg-white rounded"><Edit2 className="w-4 h-4 text-slate-400 hover:text-indigo-600" /></button>
                            <button onClick={() => openDeleteConfirm(meeting, 'meeting')} className="p-1 hover:bg-white rounded"><Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {meetings.length > 20 && <p className="text-center text-slate-500 text-sm">+{meetings.length - 20} más</p>}
                  </div>
                )}
              </TabsContent>

              {/* Alerts Tab */}
              <TabsContent value="alerts" className="space-y-4">
                {/* Tab Description */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-amber-900 mb-1">🔔 Sistema de Alertas y Recordatorios</h4>
                  <p className="text-sm text-amber-700">Configura recordatorios para no olvidar seguimientos importantes. Las alertas te ayudan a mantener el contacto con el lead en los momentos clave.</p>
                  <div className="mt-2 text-xs text-amber-800 space-y-1">
                    <p><strong>🔴 Alta prioridad:</strong> Seguimiento urgente, fecha de cierre próxima, cliente importante.</p>
                    <p><strong>🟡 Media prioridad:</strong> Seguimiento regular, recordatorio de propuesta pendiente.</p>
                    <p><strong>🟢 Baja prioridad:</strong> Recordatorio general, fecha lejana.</p>
                  </div>
                  <p className="text-xs text-amber-600 mt-2"><strong>Recomendación:</strong> Crea una alerta cada vez que prometas contactar al cliente en una fecha específica.</p>
                </div>
                <button onClick={() => { setEditingItem(null); setEditingType(null); setAlertForm({ title: '', description: '', date: '', time: '', priority: 'medium', sendEmail: false }); openModal(setShowAlertModal); }}
                  className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors">
                  <Plus className="w-4 h-4" /> Crear Alerta
                </button>
                {loadingDetails ? (
                  <div className="text-center py-8 text-slate-500">Cargando...</div>
                ) : alerts.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No hay alertas</p>
                ) : (
                  <div className="space-y-3">
                    {alerts.slice(0, 20).map((alert) => (
                      <div key={alert.id} className={`rounded-lg p-4 group ${alert.isRead ? 'bg-slate-50' : 'bg-amber-50 border border-amber-200'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Bell className={`w-4 h-4 ${alert.priority === 'high' ? 'text-red-500' : alert.priority === 'low' ? 'text-green-500' : 'text-orange-500'}`} />
                              <p className="font-medium text-slate-900">{alert.title}</p>
                            </div>
                            {alert.description && <p className="text-sm text-slate-600 mt-1">{alert.description}</p>}
                            <p className="text-xs text-slate-500 mt-1"><Clock className="w-3 h-3 inline mr-1" />{formatDate(alert.date)}</p>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => openEditModal(alert, 'alert')} className="p-1 hover:bg-white rounded"><Edit2 className="w-4 h-4 text-slate-400 hover:text-indigo-600" /></button>
                            <button onClick={() => openDeleteConfirm(alert, 'alert')} className="p-1 hover:bg-white rounded"><Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {alerts.length > 20 && <p className="text-center text-slate-500 text-sm">+{alerts.length - 20} más</p>}
                  </div>
                )}
              </TabsContent>

              {/* Tracking Tab */}
              <TabsContent value="tracking" className="space-y-4">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-900 mb-1 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" /> Datos de Atribución y Comportamiento
                  </h4>
                  <p className="text-sm text-blue-700">
                    Información capturada automáticamente cuando el lead llenó el formulario. Incluye datos de marketing, técnicos y de comportamiento.
                  </p>
                </div>
                
                {lead?.trackingData && Object.keys(lead.trackingData).length > 0 ? (
                  <div className="space-y-4">
                    {/* Channel Section - PROMINENT */}
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-5 text-white shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-emerald-100 text-sm font-medium mb-1">Canal de Origen</p>
                          <p className="text-2xl font-bold">
                            {lead.trackingData.channel || 'Canal no identificado'}
                          </p>
                          {lead.trackingData.channel_type && (
                            <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                              lead.trackingData.channel_type === 'organic' ? 'bg-green-600' :
                              lead.trackingData.channel_type === 'paid' ? 'bg-orange-500' :
                              lead.trackingData.channel_type === 'social' ? 'bg-blue-500' :
                              lead.trackingData.channel_type === 'direct' ? 'bg-slate-600' :
                              lead.trackingData.channel_type === 'ai' ? 'bg-purple-600' :
                              lead.trackingData.channel_type === 'email' ? 'bg-pink-500' :
                              lead.trackingData.channel_type === 'referral' ? 'bg-cyan-500' :
                              'bg-gray-500'
                            }`}>
                              {lead.trackingData.channel_type === 'organic' ? '🌿 Orgánico' :
                               lead.trackingData.channel_type === 'paid' ? '💰 Pagado' :
                               lead.trackingData.channel_type === 'social' ? '📱 Social' :
                               lead.trackingData.channel_type === 'direct' ? '🎯 Directo' :
                               lead.trackingData.channel_type === 'ai' ? '🤖 IA' :
                               lead.trackingData.channel_type === 'email' ? '📧 Email' :
                               lead.trackingData.channel_type === 'referral' ? '🔗 Referido' :
                               '❓ Desconocido'}
                            </span>
                          )}
                          {/* Información de quién guardó el canal manualmente */}
                          {lead.trackingData.channel_updated_by && (
                            <div className="mt-3 pt-3 border-t border-white/20">
                              <p className="text-emerald-100 text-xs flex items-center gap-2">
                                <User className="w-3 h-3" />
                                Actualizado por: <span className="font-semibold text-white">{lead.trackingData.channel_updated_by}</span>
                              </p>
                              {lead.trackingData.channel_updated_at && (
                                <p className="text-emerald-100 text-xs flex items-center gap-2 mt-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(lead.trackingData.channel_updated_at).toLocaleDateString('es-ES', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="bg-white/20 p-3 rounded-xl">
                          <Globe className="w-8 h-8" />
                        </div>
                      </div>
                    </div>

                    {/* Referidor Info Section - Only for referral leads */}
                    {lead.trackingData.channel === 'Referido' && (
                      <div className="bg-white border border-purple-200 rounded-lg overflow-hidden">
                        <div className="bg-purple-50 px-4 py-2 border-b border-purple-100 flex items-center gap-2">
                          <Users className="w-4 h-4 text-purple-600" />
                          <span className="font-medium text-purple-900">Información del Referidor</span>
                        </div>
                        <div className="p-4 space-y-3">
                          {lead.trackingData.referrer_name && (
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <span className="text-xs text-slate-500">Referidor</span>
                                <p className="text-sm font-medium text-slate-900">{lead.trackingData.referrer_name}</p>
                              </div>
                            </div>
                          )}
                          {lead.trackingData.referrer_email && (
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Mail className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <span className="text-xs text-slate-500">Email del Referidor</span>
                                <p className="text-sm font-medium text-slate-900">{lead.trackingData.referrer_email}</p>
                              </div>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                            {lead.trackingData.submitted_date && (
                              <div>
                                <span className="text-xs text-slate-500">Fecha de Registro</span>
                                <p className="text-sm font-medium text-slate-900">{lead.trackingData.submitted_date}</p>
                              </div>
                            )}
                            {lead.trackingData.submitted_time && (
                              <div>
                                <span className="text-xs text-slate-500">Hora</span>
                                <p className="text-sm font-medium text-slate-900">{lead.trackingData.submitted_time}</p>
                              </div>
                            )}
                            {lead.trackingData.device_type && (
                              <div>
                                <span className="text-xs text-slate-500">Dispositivo</span>
                                <p className="text-sm font-medium text-slate-900 flex items-center gap-1">
                                  {lead.trackingData.device_type === 'Mobile' ? (
                                    <><Smartphone className="w-4 h-4" /> Celular</>
                                  ) : (
                                    <><Monitor className="w-4 h-4" /> PC</>
                                  )}
                                </p>
                              </div>
                            )}
                            {lead.trackingData.ip_address && (
                              <div>
                                <span className="text-xs text-slate-500">IP</span>
                                <p className="text-sm font-medium text-slate-900">{lead.trackingData.ip_address}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Attribution Section */}
                    {(lead.trackingData.utm_source || lead.trackingData.utm_medium || lead.trackingData.utm_campaign || lead.trackingData.gclid || lead.trackingData.fbclid || lead.trackingData.referrer_url) && (
                      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                        <div className="bg-blue-50 px-4 py-2 border-b border-blue-100 flex items-center gap-2">
                          <Tag className="w-4 h-4 text-blue-600" />
                          <span className="font-medium text-blue-900">Atribución (Marketing)</span>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-3">
                          {lead.trackingData.utm_source && (
                            <div>
                              <span className="text-xs text-slate-500">UTM Source</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.utm_source}</p>
                            </div>
                          )}
                          {lead.trackingData.utm_medium && (
                            <div>
                              <span className="text-xs text-slate-500">UTM Medium</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.utm_medium}</p>
                            </div>
                          )}
                          {lead.trackingData.utm_campaign && (
                            <div>
                              <span className="text-xs text-slate-500">UTM Campaña</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.utm_campaign}</p>
                            </div>
                          )}
                          {lead.trackingData.utm_term && (
                            <div>
                              <span className="text-xs text-slate-500">Término</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.utm_term}</p>
                            </div>
                          )}
                          {lead.trackingData.utm_content && (
                            <div>
                              <span className="text-xs text-slate-500">Contenido</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.utm_content}</p>
                            </div>
                          )}
                          {lead.trackingData.gclid && (
                            <div>
                              <span className="text-xs text-slate-500">Google Click ID</span>
                              <p className="text-sm font-medium text-slate-900 truncate" title={lead.trackingData.gclid}>{lead.trackingData.gclid.substring(0, 20)}...</p>
                            </div>
                          )}
                          {lead.trackingData.fbclid && (
                            <div>
                              <span className="text-xs text-slate-500">Facebook Click ID</span>
                              <p className="text-sm font-medium text-slate-900 truncate" title={lead.trackingData.fbclid}>{lead.trackingData.fbclid.substring(0, 20)}...</p>
                            </div>
                          )}
                          {lead.trackingData.referrer_url && (
                            <div className="col-span-2">
                              <span className="text-xs text-slate-500">URL de Referencia</span>
                              <p className="text-sm font-medium text-slate-900 truncate" title={lead.trackingData.referrer_url}>{lead.trackingData.referrer_url}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Technical Section */}
                    {(lead.trackingData.device_type || lead.trackingData.browser_language || lead.trackingData.screen_resolution || lead.trackingData.user_agent) && (
                      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                        <div className="bg-purple-50 px-4 py-2 border-b border-purple-100 flex items-center gap-2">
                          <Monitor className="w-4 h-4 text-purple-600" />
                          <span className="font-medium text-purple-900">Datos Técnicos</span>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-3">
                          {lead.trackingData.device_type && (
                            <div>
                              <span className="text-xs text-slate-500">Dispositivo</span>
                              <p className="text-sm font-medium text-slate-900 capitalize">{lead.trackingData.device_type}</p>
                            </div>
                          )}
                          {lead.trackingData.browser_language && (
                            <div>
                              <span className="text-xs text-slate-500">Idioma</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.browser_language}</p>
                            </div>
                          )}
                          {lead.trackingData.screen_resolution && (
                            <div>
                              <span className="text-xs text-slate-500">Resolución</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.screen_resolution}</p>
                            </div>
                          )}
                          {lead.trackingData.connection_type && (
                            <div>
                              <span className="text-xs text-slate-500">Conexión</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.connection_type}</p>
                            </div>
                          )}
                          {lead.trackingData.ip_address && (
                            <div>
                              <span className="text-xs text-slate-500">IP</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.ip_address}</p>
                            </div>
                          )}
                          {lead.trackingData.user_agent && (
                            <div className="col-span-2">
                              <span className="text-xs text-slate-500">User Agent</span>
                              <p className="text-xs text-slate-600 truncate" title={lead.trackingData.user_agent}>{lead.trackingData.user_agent}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Behavior Section */}
                    {(lead.trackingData.first_touch_url || lead.trackingData.conversion_url || lead.trackingData.visit_count || lead.trackingData.time_on_page_sec || lead.trackingData.scroll_depth_pct) && (
                      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                        <div className="bg-green-50 px-4 py-2 border-b border-green-100 flex items-center gap-2">
                          <MousePointer className="w-4 h-4 text-green-600" />
                          <span className="font-medium text-green-900">Comportamiento</span>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-3">
                          {lead.trackingData.visit_count && (
                            <div>
                              <span className="text-xs text-slate-500">Visitas</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.visit_count}</p>
                            </div>
                          )}
                          {lead.trackingData.time_on_page_sec && (
                            <div>
                              <span className="text-xs text-slate-500">Tiempo en página</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.time_on_page_sec}s</p>
                            </div>
                          )}
                          {lead.trackingData.scroll_depth_pct && (
                            <div>
                              <span className="text-xs text-slate-500">Scroll</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.scroll_depth_pct}%</p>
                            </div>
                          )}
                          {lead.trackingData.saw_whole_page !== undefined && (
                            <div>
                              <span className="text-xs text-slate-500">Vio toda la página</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.saw_whole_page ? 'Sí' : 'No'}</p>
                            </div>
                          )}
                          {lead.trackingData.first_touch_url && (
                            <div className="col-span-2">
                              <span className="text-xs text-slate-500">Landing Page</span>
                              <p className="text-sm font-medium text-slate-900 truncate" title={lead.trackingData.first_touch_url}>{lead.trackingData.first_touch_url}</p>
                            </div>
                          )}
                          {lead.trackingData.conversion_url && (
                            <div className="col-span-2">
                              <span className="text-xs text-slate-500">Página de conversión</span>
                              <p className="text-sm font-medium text-slate-900 truncate" title={lead.trackingData.conversion_url}>{lead.trackingData.conversion_url}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Quality Section */}
                    {(lead.trackingData.form_fill_duration || lead.trackingData.email_pasted !== undefined || lead.trackingData.is_bot_honeypot !== undefined || lead.trackingData.email_domain_type) && (
                      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                        <div className="bg-orange-50 px-4 py-2 border-b border-orange-100 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-orange-600" />
                          <span className="font-medium text-orange-900">Calidad del Formulario</span>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-3">
                          {lead.trackingData.form_fill_duration && (
                            <div>
                              <span className="text-xs text-slate-500">Tiempo de llenado</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.form_fill_duration}s</p>
                            </div>
                          )}
                          {lead.trackingData.email_domain_type && (
                            <div>
                              <span className="text-xs text-slate-500">Tipo de email</span>
                              <p className="text-sm font-medium text-slate-900 capitalize">{lead.trackingData.email_domain_type}</p>
                            </div>
                          )}
                          {lead.trackingData.email_pasted !== undefined && (
                            <div>
                              <span className="text-xs text-slate-500">Email pegado</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.email_pasted ? 'Sí' : 'No'}</p>
                            </div>
                          )}
                          {lead.trackingData.used_autofill !== undefined && (
                            <div>
                              <span className="text-xs text-slate-500">Autofill usado</span>
                              <p className="text-sm font-medium text-slate-900">{lead.trackingData.used_autofill ? 'Sí' : 'No'}</p>
                            </div>
                          )}
                          {lead.trackingData.is_bot_honeypot !== undefined && (
                            <div>
                              <span className="text-xs text-slate-500">Detección Bot</span>
                              <p className={`text-sm font-medium ${lead.trackingData.is_bot_honeypot ? 'text-red-600' : 'text-green-600'}`}>
                                {lead.trackingData.is_bot_honeypot ? '⚠️ Posible Bot' : '✓ Humano'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Context Section */}
                    {lead.trackingData.user_local_time && (
                      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                        <div className="bg-pink-50 px-4 py-2 border-b border-pink-100 flex items-center gap-2">
                          <Globe className="w-4 h-4 text-pink-600" />
                          <span className="font-medium text-pink-900">Contexto</span>
                        </div>
                        <div className="p-4">
                          <span className="text-xs text-slate-500">Hora local del usuario</span>
                          <p className="text-sm font-medium text-slate-900">{new Date(lead.trackingData.user_local_time).toLocaleString()}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-medium">Sin datos de tracking</p>
                    <p className="text-sm text-slate-400 mt-1">Este lead no tiene datos de atribución capturados.</p>
                    <p className="text-xs text-slate-400 mt-2">Los datos se capturan automáticamente cuando el lead llena un formulario con el script de tracking instalado.</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </Drawer.Content>
      </Drawer.Portal>

      {/* Activity Modal */}
      <ModalPortal isOpen={showActivityModal}>
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">{editingItem ? 'Editar Actividad' : 'Nueva Actividad'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tipo</label>
                <select value={activityForm.type} onChange={(e) => setActivityForm({ ...activityForm, type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg">
                  <option value="note">Nota</option>
                  <option value="call">Llamada</option>
                  <option value="email">Email</option>
                  <option value="meeting">Reunión</option>
                  <option value="task">Tarea</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                <textarea value={activityForm.description} onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                  rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg" placeholder="Describe la actividad..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha de la actividad</label>
                <input type="date" value={activityForm.activityDate} onChange={(e) => setActivityForm({ ...activityForm, activityDate: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { closeModal(setShowActivityModal); setEditingItem(null); }} className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200">Cancelar</button>
              <button onClick={editingItem ? handleEditActivity : handleAddActivity} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{editingItem ? 'Actualizar' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      </ModalPortal>

      {/* File Modal */}
      <ModalPortal isOpen={showFileModal}>
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">{editingItem ? 'Editar Archivo' : 'Nuevo Archivo/Enlace'}</h3>
            <div className="space-y-4">
              {/* Tipo de archivo - tabs */}
              {!editingItem && (
                <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                  <button
                    onClick={() => setFileForm({ ...fileForm, type: 'link', fileData: null, file: null })}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${fileForm.type === 'link' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    <LinkIcon className="w-4 h-4 inline mr-1" /> Enlace URL
                  </button>
                  <button
                    onClick={() => setFileForm({ ...fileForm, type: 'upload', url: '' })}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${fileForm.type === 'upload' ? 'bg-white text-green-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    <Upload className="w-4 h-4 inline mr-1" /> Subir Archivo
                  </button>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                <input type="text" value={fileForm.name} onChange={(e) => setFileForm({ ...fileForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg" placeholder="Nombre del archivo" />
              </div>
              
              {fileForm.type === 'link' ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">URL</label>
                  <input type="url" value={fileForm.url} onChange={(e) => setFileForm({ ...fileForm, url: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" placeholder="https://..." />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Archivo</label>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-indigo-300 transition-colors">
                    {fileForm.fileData ? (
                      <div className="flex items-center justify-center gap-3">
                        <FileText className="w-8 h-8 text-indigo-600" />
                        <div className="text-left">
                          <p className="text-sm font-medium text-slate-900">{fileForm.name || 'Archivo seleccionado'}</p>
                          <p className="text-xs text-slate-500">{fileForm.fileType}</p>
                        </div>
                        <button onClick={() => setFileForm({ ...fileForm, fileData: null, file: null, fileType: null })}
                          className="p-1 text-red-500 hover:bg-red-50 rounded">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <input type="file" className="hidden" onChange={handleFileSelect} 
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.zip,.rar,.txt,.csv" />
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600">Click para seleccionar archivo</p>
                        <p className="text-xs text-slate-400 mt-1">PDF, Word, Excel, imágenes, ZIP (máx 10MB)</p>
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { closeModal(setShowFileModal); setEditingItem(null); setFileForm({ name: '', url: '', type: 'link', file: null, fileData: null, fileType: null }); }} className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200">Cancelar</button>
              <button onClick={editingItem ? handleEditFile : handleAddFile} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{editingItem ? 'Actualizar' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      </ModalPortal>

      {/* Meeting Modal */}
      <ModalPortal isOpen={showMeetingModal}>
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl m-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">{editingItem ? 'Editar Reunión' : 'Nueva Reunión'}</h3>
            <div className="space-y-4">
              {/* Row 1: Título y Fecha */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Título</label>
                  <input type="text" value={meetingForm.title} onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" placeholder="Título de la reunión" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fecha y Hora</label>
                  <input type="datetime-local" value={meetingForm.date} onChange={(e) => setMeetingForm({ ...meetingForm, date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                </div>
              </div>
              
              {/* Row 2: Duración y Tipo */}
              <div className="grid grid-cols-2 gap-4">
                {/* Duración */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Duración</label>
                  <div className="flex gap-1">
                    {[15, 30, 45, 60].map(mins => (
                      <button
                        key={mins}
                        type="button"
                        onClick={() => setMeetingForm({ ...meetingForm, duration: mins })}
                        className={`flex-1 px-2 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                          meetingForm.duration === mins
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-slate-200 hover:border-slate-300 text-slate-600'
                        }`}
                      >
                        {mins}m
                      </button>
                    ))}
                  </div>
                </div>
              
                {/* Tipo de Reunión */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tipo</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setMeetingForm({ ...meetingForm, meetingType: 'virtual', location: '' })}
                      className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg border-2 transition-all ${
                        meetingForm.meetingType === 'virtual' 
                          ? 'border-purple-500 bg-purple-50 text-purple-700' 
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                    >
                      <Video className="w-4 h-4" />
                      <span className="font-medium text-sm">Virtual</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setMeetingForm({ ...meetingForm, meetingType: 'presencial', videoLink: '' })}
                      className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg border-2 transition-all ${
                        meetingForm.meetingType === 'presencial' 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                    >
                      <Building2 className="w-4 h-4" />
                      <span className="font-medium text-sm">Presencial</span>
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Link o Ubicación según tipo */}
              {meetingForm.meetingType === 'virtual' ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Link de videollamada</label>
                  <input type="url" value={meetingForm.videoLink} onChange={(e) => setMeetingForm({ ...meetingForm, videoLink: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" placeholder="https://meet.google.com/... o https://zoom.us/..." />
                  <p className="text-xs text-slate-500 mt-1">Se generará automáticamente con Google Calendar si está conectado</p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ubicación / Dirección *</label>
                  <input type="text" value={meetingForm.location} onChange={(e) => setMeetingForm({ ...meetingForm, location: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" placeholder="Ej: Oficina principal, Cra 7 #123-45, Bogotá" />
                </div>
              )}
              
              {/* Row 3: Descripción y Asistentes */}
              <div className="grid grid-cols-2 gap-4">
                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                  <textarea 
                    value={meetingForm.description} 
                    onChange={(e) => setMeetingForm({ ...meetingForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg resize-none" 
                    rows={2}
                    placeholder="Agenda o notas..." 
                  />
                </div>
              
                {/* Asistentes */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Asistentes adicionales</label>
                  <div className="mb-1">
                    {lead?.email && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                        👤 {lead.email}
                      </span>
                    )}
                  </div>
                  <input 
                    type="text" 
                    value={meetingForm.additionalAttendees} 
                    onChange={(e) => setMeetingForm({ ...meetingForm, additionalAttendees: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" 
                    placeholder="correo1@ej.com, correo2@ej.com" 
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => { closeModal(setShowMeetingModal); setEditingItem(null); }} className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200">Cancelar</button>
              <button onClick={editingItem ? handleEditMeeting : handleAddMeeting} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{editingItem ? 'Actualizar' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      </ModalPortal>

      {/* Alert Modal */}
      <ModalPortal isOpen={showAlertModal}>
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">{editingItem ? 'Editar Alerta' : 'Nueva Alerta'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Título</label>
                <input type="text" value={alertForm.title} onChange={(e) => setAlertForm({ ...alertForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg" placeholder="Título de la alerta" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Descripción</label>
                <textarea value={alertForm.description} onChange={(e) => setAlertForm({ ...alertForm, description: e.target.value })}
                  rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg" placeholder="Descripción..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fecha</label>
                  <input type="date" value={alertForm.date} onChange={(e) => setAlertForm({ ...alertForm, date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prioridad</label>
                  <select value={alertForm.priority} onChange={(e) => setAlertForm({ ...alertForm, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg">
                    <option value="low">Baja</option>
                    <option value="medium">Media</option>
                    <option value="high">Alta</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { closeModal(setShowAlertModal); setEditingItem(null); }} className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200">Cancelar</button>
              <button onClick={editingItem ? handleEditAlert : handleAddAlert} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{editingItem ? 'Actualizar' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      </ModalPortal>

      {/* Form History Modal */}
      <ModalPortal isOpen={showFormHistoryModal}>
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl m-4 max-h-[85vh] flex flex-col">
            <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Historial de Registros</h3>
                    <p className="text-sm text-slate-500">
                      {formHistory?.totalRegistrations > 0 
                        ? `${formHistory.totalRegistrations} registro(s) con el email ${formHistory.leadEmail}`
                        : 'Todos los registros asociados a este contacto'}
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowFormHistoryModal(false)} className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {loadingFormHistory ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-slate-500 mt-2">Cargando historial...</p>
                </div>
              ) : formHistory && formHistory.history?.length > 0 ? (
                <div className="space-y-4">
                  {formHistory.history.map((entry, index) => {
                    const pipelineInfo = formHistory.pipelines?.[entry.pipelineId];
                    const customFields = pipelineInfo?.customFields || [];
                    
                    return (
                      <div 
                        key={entry.id || index} 
                        className={`rounded-xl border-2 overflow-hidden ${entry.isCurrent ? 'border-purple-400 shadow-md shadow-purple-100' : 'border-slate-200'}`}
                      >
                        {/* Header con información principal */}
                        <div className={`p-4 ${entry.isCurrent ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white' : 'bg-slate-50'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${entry.isCurrent ? 'bg-white/20' : 'bg-slate-200 text-slate-600'}`}>
                                {index + 1}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold">{entry.pipelineName}</span>
                                  {entry.isCurrent && (
                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-white/20">
                                      Registro Actual
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span 
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                                    style={{
                                      backgroundColor: entry.isCurrent ? 'rgba(255,255,255,0.2)' : (entry.stageColor || '#6366f1') + '20',
                                      color: entry.isCurrent ? 'white' : (entry.stageColor || '#6366f1')
                                    }}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry.isCurrent ? 'white' : (entry.stageColor || '#6366f1') }}></span>
                                    {entry.stageName || 'Sin etapa'}
                                  </span>
                                  {entry.source && (
                                    <span className={`text-xs ${entry.isCurrent ? 'text-white/70' : 'text-slate-500'}`}>
                                      vía {entry.source}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`text-sm font-medium ${entry.isCurrent ? 'text-white' : 'text-slate-700'}`}>
                                {entry.date ? new Date(entry.date).toLocaleDateString('es-CO', { 
                                  day: '2-digit', 
                                  month: 'short', 
                                  year: 'numeric'
                                }) : 'Sin fecha'}
                              </p>
                              <p className={`text-xs ${entry.isCurrent ? 'text-white/70' : 'text-slate-500'}`}>
                                {entry.date ? new Date(entry.date).toLocaleTimeString('es-CO', { 
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : ''}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Detalles del registro */}
                        <div className="p-4 space-y-3">
                          {/* Info básica del lead en este registro */}
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                            {entry.name && (
                              <div>
                                <span className="text-slate-500 text-xs">Nombre</span>
                                <p className="font-medium text-slate-900 truncate">{entry.name}</p>
                              </div>
                            )}
                            {entry.phone && (
                              <div>
                                <span className="text-slate-500 text-xs">Teléfono</span>
                                <p className="font-medium text-slate-900">{entry.phone}</p>
                              </div>
                            )}
                            {entry.value > 0 && (
                              <div>
                                <span className="text-slate-500 text-xs">Valor</span>
                                <p className="font-medium text-green-600">
                                  {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(entry.value)}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {/* Campañas */}
                          {entry.campaignTags && entry.campaignTags.length > 0 && (
                            <div>
                              <span className="text-slate-500 text-xs">Campañas</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {entry.campaignTags.map((ct, idx) => (
                                  <span key={idx} className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700">
                                    {ct.name || ct.tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Campos personalizados */}
                          {entry.fields && Object.keys(entry.fields).length > 0 && (
                            <div className="pt-2 border-t border-slate-100">
                              <p className="text-xs text-slate-500 mb-2 font-medium">Campos del formulario:</p>
                              <div className="bg-slate-50 rounded-lg divide-y divide-slate-100">
                                {Object.entries(entry.fields).map(([key, value]) => {
                                  // Try to find field label from custom fields definition
                                  const fieldDef = customFields.find(f => f.id === key);
                                  const fieldLabel = fieldDef?.label || key;
                                  
                                  // Format value for display
                                  let displayValue = value;
                                  if (typeof value === 'boolean') {
                                    displayValue = value ? 'Sí' : 'No';
                                  } else if (Array.isArray(value)) {
                                    displayValue = value.join(', ');
                                  } else if (value === null || value === undefined || value === '') {
                                    displayValue = <span className="text-slate-400 italic">Sin valor</span>;
                                  }
                                  
                                  // Check if it's a long text field
                                  const isLongText = typeof displayValue === 'string' && displayValue.length > 50;
                                  
                                  return (
                                    <div key={key} className={`py-2 px-3 ${isLongText ? '' : 'flex items-center justify-between'}`}>
                                      <span className="text-xs font-medium text-slate-600 block">{fieldLabel}</span>
                                      {isLongText ? (
                                        <p className="text-sm text-slate-900 mt-1 whitespace-pre-wrap break-words">{displayValue}</p>
                                      ) : (
                                        <span className="text-sm text-slate-900 text-right">{displayValue}</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Notas */}
                          {entry.notes && (
                            <div className="pt-2 border-t border-slate-100">
                              <p className="text-xs text-slate-500 mb-1 font-medium">Notas:</p>
                              <p className="text-sm text-slate-700 bg-yellow-50 border border-yellow-100 rounded-lg p-2">{entry.notes}</p>
                            </div>
                          )}
                          
                          {/* Link to view this lead */}
                          {!entry.isCurrent && entry.id && (
                            <div className="pt-2">
                              <a
                                href={`/lead/${entry.id}`}
                                className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                              >
                                <ExternalLink className="w-3 h-3" />
                                Ver este registro
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No hay historial de registros</p>
                  <p className="text-sm text-slate-400 mt-1">Los registros con este email aparecerán aquí</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <button 
                onClick={() => setShowFormHistoryModal(false)} 
                className="w-full px-4 py-2 text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 font-medium"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </ModalPortal>

      {/* Consolidated View Modal */}
      <ModalPortal isOpen={showConsolidatedModal}>
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl m-4 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-blue-500 to-indigo-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-xl backdrop-blur">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="text-xl font-bold">Vista Consolidada</h3>
                    <p className="text-sm text-white/80 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {consolidatedData?.email}
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowConsolidatedModal(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              
              {/* Summary Stats */}
              {consolidatedData?.summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{consolidatedData.summary.totalLeads}</p>
                    <p className="text-xs text-white/80">Leads</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{consolidatedData.summary.totalPipelines}</p>
                    <p className="text-xs text-white/80">Embudos</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{consolidatedData.summary.totalActivities}</p>
                    <p className="text-xs text-white/80">Actividades</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{consolidatedData.summary.totalDeals}</p>
                    <p className="text-xs text-white/80">Negocios</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingConsolidated ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-slate-500 mt-2">Consolidando información...</p>
                </div>
              ) : consolidatedData ? (
                <div className="space-y-6">
                  {/* Leads en diferentes embudos */}
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      Leads en Diferentes Embudos ({consolidatedData.leads?.length || 0})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {consolidatedData.leads?.map((l, idx) => (
                        <div 
                          key={l.id || idx}
                          onClick={() => {
                            setShowConsolidatedModal(false);
                            navigate(`/lead/${l.id}`);
                          }}
                          className={`p-4 rounded-xl border cursor-pointer hover:shadow-md transition-all ${
                            l.id === leadId 
                              ? 'border-blue-300 bg-blue-50' 
                              : 'border-slate-200 bg-white hover:border-blue-200'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-900">{l.name}</span>
                                {l.id === leadId && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500 text-white">Actual</span>
                                )}
                              </div>
                              <p className="text-sm text-slate-500 mt-1">{l.pipelineName}</p>
                            </div>
                            <span 
                              className="px-2 py-1 text-xs font-medium rounded-full"
                              style={{ backgroundColor: `${l.stageColor}20`, color: l.stageColor }}
                            >
                              {l.stageName}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {l.createdAt ? new Date(l.createdAt).toLocaleDateString('es-ES') : 'Sin fecha'}
                            </span>
                            {l.value > 0 && (
                              <span className="text-emerald-600 font-medium">
                                ${l.value?.toLocaleString()}
                              </span>
                            )}
                          </div>
                          
                          {/* Custom Fields Preview */}
                          {(l.customFields || l.data) && Object.keys({...l.data, ...l.customFields}).length > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <p className="text-xs font-medium text-slate-500 mb-2">Campos registrados:</p>
                              <div className="flex flex-wrap gap-1">
                                {Object.entries({...l.data, ...l.customFields}).slice(0, 4).map(([key, value]) => (
                                  <span key={key} className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                                    {key}: {typeof value === 'string' ? value.substring(0, 20) : String(value)}
                                  </span>
                                ))}
                                {Object.keys({...l.data, ...l.customFields}).length > 4 && (
                                  <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-500 rounded">
                                    +{Object.keys({...l.data, ...l.customFields}).length - 4} más
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Actividades recientes */}
                  {consolidatedData.activities?.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-purple-600" />
                        Actividades Recientes ({consolidatedData.activities.length})
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {consolidatedData.activities.slice(0, 10).map((activity, idx) => (
                          <div key={activity.id || idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <MessageSquare className="w-4 h-4 text-purple-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-900">{activity.description}</p>
                              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                <span>{activity.pipelineName}</span>
                                <span>•</span>
                                <span>{activity.createdAt ? new Date(activity.createdAt).toLocaleDateString('es-ES') : ''}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Reuniones */}
                  {consolidatedData.meetings?.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Video className="w-5 h-5 text-green-600" />
                        Reuniones ({consolidatedData.meetings.length})
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {consolidatedData.meetings.slice(0, 6).map((meeting, idx) => (
                          <div key={meeting.id || idx} className="p-3 bg-green-50 rounded-lg border border-green-100">
                            <p className="font-medium text-slate-900">{meeting.title}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                              <Calendar className="w-3 h-3" />
                              <span>{meeting.date ? new Date(meeting.date).toLocaleDateString('es-ES') : ''}</span>
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                meeting.meetingType === 'virtual' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {meeting.meetingType === 'virtual' ? 'Virtual' : 'Presencial'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Negocios */}
                  {consolidatedData.deals?.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-emerald-600" />
                        Negocios ({consolidatedData.deals.length})
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {consolidatedData.deals.map((deal, idx) => (
                          <div key={deal.id || idx} className={`p-3 rounded-lg border ${
                            deal.status === 'won' ? 'bg-emerald-50 border-emerald-200' :
                            deal.status === 'lost' ? 'bg-red-50 border-red-200' :
                            'bg-slate-50 border-slate-200'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {deal.code && (
                                  <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded font-semibold">{deal.code}</span>
                                )}
                                <span className="font-medium text-slate-900">{deal.empresaName || 'Negocio'}</span>
                              </div>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                deal.status === 'won' ? 'bg-emerald-100 text-emerald-700' :
                                deal.status === 'lost' ? 'bg-red-100 text-red-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {deal.status === 'won' ? 'Ganado' : deal.status === 'lost' ? 'Perdido' : 'En Proceso'}
                              </span>
                            </div>
                            <p className="text-lg font-bold text-emerald-600 mt-1">
                              ${(deal.totalValue || 0).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Timeline Info */}
                  {consolidatedData.summary?.firstInteraction && (
                    <div className="bg-slate-50 rounded-xl p-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Línea de Tiempo</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-slate-500">Primera Interacción</p>
                          <p className="font-medium text-slate-900">
                            {new Date(consolidatedData.summary.firstInteraction).toLocaleDateString('es-ES', {
                              day: '2-digit', month: 'long', year: 'numeric'
                            })}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Última Interacción</p>
                          <p className="font-medium text-slate-900">
                            {consolidatedData.summary.lastInteraction ? new Date(consolidatedData.summary.lastInteraction).toLocaleDateString('es-ES', {
                              day: '2-digit', month: 'long', year: 'numeric'
                            }) : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No se encontraron datos</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <button 
                onClick={() => setShowConsolidatedModal(false)} 
                className="w-full px-4 py-2 text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 font-medium"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </ModalPortal>

      {/* Delete Confirmation */}
      <DeleteConfirmModal
        isOpen={!!deletingItem}
        onClose={() => { 
          setDeletingItem(null); 
          setDeletingType(null); 
          setTimeout(() => setIsDrawerOpen(true), 100);
        }}
        onConfirm={() => {
          handleDeleteConfirm();
          setTimeout(() => setIsDrawerOpen(true), 100);
        }}
        itemType={deletingType === 'activity' ? 'la actividad' : deletingType === 'file' ? 'el archivo' : deletingType === 'meeting' ? 'la reunión' : 'la alerta'}
        itemName={deletingItem?.title || deletingItem?.name || deletingItem?.description?.substring(0, 30) || ''}
      />

      {/* Edit Lead Modal */}
      <LeadFormModal 
        isOpen={showEditModal} 
        onClose={() => { setShowEditModal(false); setTimeout(() => setIsDrawerOpen(true), 100); }} 
        onSuccess={(updatedLead) => {
          // Update lead state with new data
          setLead(prev => ({ ...prev, ...updatedLead }));
        }}
        leadToEdit={lead} 
      />

      {/* Delete Lead Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteLead}
        itemType="el lead"
        itemName={lead.name}
      />

      {/* Create Deal Modal */}
      <ModalPortal isOpen={showDealModal}>
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 border-b border-slate-200 z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-emerald-600" />
                  Crear Negocio
                </h3>
                <button onClick={() => setShowDealModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* Show empresa info prominently */}
              <div className="mt-3 p-3 bg-indigo-50 rounded-lg">
                <p className="text-xs text-indigo-600 font-medium mb-1">🏢 EMPRESA</p>
                <p className="font-semibold text-indigo-900">{empresaAsociada?.name || 'N/A'}</p>
                <p className="text-xs text-indigo-700">{empresaAsociada?.documentType}: {empresaAsociada?.documentNumber}</p>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Contacto: {lead?.name}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Servicios */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Servicios <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {availableServices.filter(s => !dealForm.services.find(ds => ds.id === s.id || ds.name === s.name)).map(service => (
                    <button
                      key={service.id || service.name}
                      type="button"
                      onClick={() => addServiceToDeal(service)}
                      className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-full transition-colors"
                    >
                      + {service.name}
                    </button>
                  ))}
                </div>
                
                {dealForm.services.length > 0 && (
                  <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                    {dealForm.services.map((service, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium flex-1">{service.name}</span>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">Cant:</span>
                            <input
                              type="number"
                              min="1"
                              value={service.quantity}
                              onChange={(e) => updateServiceQuantity(idx, e.target.value)}
                              className="w-16 px-2 py-1 border border-slate-200 rounded text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">Valor:</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={formatNumberAsCurrency(service.value || service.price || 0)}
                              onChange={(e) => {
                                const numericValue = parseCurrencyToNumber(e.target.value);
                                const newServices = [...dealForm.services];
                                newServices[idx] = { ...newServices[idx], value: numericValue };
                                setDealForm(prev => ({ ...prev, services: newServices }));
                              }}
                              className="w-32 px-2 py-1 border border-slate-200 rounded text-sm text-right"
                              placeholder="0"
                            />
                          </div>
                          <span className="text-sm font-medium text-emerald-600 w-24 text-right">
                            {formatValueShort((service.value || service.price || 0) * service.quantity)}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeServiceFromDeal(idx)}
                            className="text-red-500 hover:text-red-700 font-bold"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-slate-200 flex justify-between font-semibold">
                      <span>Total:</span>
                      <span className="text-green-600">
                        {formatValueShort(dealForm.services.reduce((acc, s) => acc + ((s.value || s.price || 0) * s.quantity), 0))}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Fecha estimada */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha Estimada de Cierre</label>
                <input
                  type="date"
                  value={dealForm.estimatedCloseDate}
                  onChange={(e) => setDealForm(prev => ({ ...prev, estimatedCloseDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>

              {/* Notas */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notas</label>
                <textarea
                  value={dealForm.notes}
                  onChange={(e) => setDealForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  rows={3}
                  placeholder="Notas adicionales..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDealModal(false)}
                className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreateDeal}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Crear Negocio
              </button>
            </div>
          </div>
        </div>
      </ModalPortal>
    </Drawer.Root>
  );
};

export default LeadDetail;
